import { Fragment, useMemo, useState } from 'react'
import { toast } from 'sonner'
import type { ContractElement } from '@/features/contract/elements/types'
import { formatCurrencyPl } from '@/features/contract/components/numberFormat'
import { NumericInputBase } from '@/features/contract/components/field-renderer/inputs/NumericInputBase'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  useAllocatePayment, useCashSplit, useContractPayments, useContractSchedule,
  useDeleteAllocation, useDeletePayment, useGenerateContractSchedule, useRegisterPayment, useRegisterRefund,
} from '../hooks'
import { SCHEDULE_BASIS_LABELS, type CashPayment, type CashScheduleItem } from '../types'

const inputCls =
  'rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm shadow-xs focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15'
const btnCls =
  'inline-flex items-center justify-center rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white shadow-xs hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed'

function today(): string { return new Date().toISOString().slice(0, 10) }

function paidOf(i: CashScheduleItem): number { return i.paidAmount ?? 0 }

function round2(n: number): number { return Math.round(n * 100) / 100 }

function formatDatePl(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

function dayWord(n: number): string { return n === 1 ? 'dzień' : 'dni' }

/** Ile dni od dziś do daty (ujemne = po terminie). */
function daysUntil(iso: string): number {
  const due = new Date(`${iso}T00:00:00`)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((due.getTime() - today.getTime()) / 86_400_000)
}

/** Relatywny termin gdy brak daty kalendarzowej (kotwica nieustalona) - czytelne frazowanie. */
function relativeTerm(i: CashScheduleItem): string {
  const anchor = i.basis === 'SIGNING' ? 'podpisania'
    : i.basis === 'DELIVERY' ? 'dostawy'
    : i.basis === 'INSTALLATION' ? 'montażu'
    : null
  if (anchor === null) return SCHEDULE_BASIS_LABELS.FIXED // 'Data stała'
  const off = i.offsetDays ?? 0
  if (off === 0) return `W dniu ${anchor}`
  if (off > 0) return `${off} ${dayWord(off)} od ${anchor}`
  return `${-off} ${dayWord(-off)} przed ${anchor}`
}

/**
 * Termin transzy do wyświetlenia: data (gdy znana) + status czasowy dla niezapłaconych
 * (po terminie / dziś / za N dni). Zapłacone i bez daty bez naglacego statusu.
 */
function termInfo(i: CashScheduleItem): { label: string; hint: string | null; cls: string } {
  const settled = (i.amount - paidOf(i)) <= 0.005
  if (!i.dueDate) {
    return { label: relativeTerm(i), hint: settled ? null : 'termin nieustalony', cls: 'text-slate-500' }
  }
  const label = formatDatePl(i.dueDate)
  if (settled) return { label, hint: null, cls: 'text-slate-500' }
  const days = daysUntil(i.dueDate)
  if (days < 0) return { label, hint: `po terminie ${-days} ${dayWord(-days)}`, cls: 'text-rose-700' }
  if (days === 0) return { label, hint: 'termin dziś', cls: 'text-rose-700' }
  if (days <= 7) return { label, hint: `za ${days} ${dayWord(days)}`, cls: 'text-amber-700' }
  return { label, hint: `za ${days} ${dayWord(days)}`, cls: 'text-slate-600' }
}

function status(i: CashScheduleItem): { label: string; cls: string } {
  const paid = paidOf(i)
  if (paid <= 0) return { label: 'Oczekuje', cls: 'text-slate-500' }
  if (paid >= i.amount - 0.005) return { label: 'Zapłacono', cls: 'text-emerald-700' }
  return { label: `Częściowo ${formatCurrencyPl(paid)}`, cls: 'text-amber-700' }
}

/**
 * Status alokacji wpłaty. canAllocate = czy są jeszcze niezapłacone transze, na które można
 * dopiąć resztę. Gdy reszta jest, ale nie ma gdzie jej alokować → nadpłata do zwrotu.
 */
function paymentStatus(p: CashPayment, canAllocate: boolean): { label: string; cls: string; needsAlloc: boolean } {
  if (p.unallocatedAmount <= 0.005) return { label: 'Zaalokowana', cls: 'text-emerald-700', needsAlloc: false }
  if (!canAllocate) {
    return { label: `Nadpłata ${formatCurrencyPl(p.unallocatedAmount)} — do zwrotu`, cls: 'text-rose-700 font-semibold', needsAlloc: false }
  }
  if (p.allocatedAmount <= 0.005) return { label: 'Niezaalokowana', cls: 'text-amber-700', needsAlloc: true }
  return { label: `Zostało ${formatCurrencyPl(p.unallocatedAmount)} do alokacji`, cls: 'text-amber-700', needsAlloc: true }
}

export function ContractCashSchedule({
  contractId, cashTotal, elements,
}: { contractId: number; cashTotal: number; elements: ContractElement[] }) {
  const scheduleQ = useContractSchedule(contractId)
  const paymentsQ = useContractPayments(contractId)
  const splitQ = useCashSplit(contractId, cashTotal)
  const generate = useGenerateContractSchedule(contractId)
  const register = useRegisterPayment(contractId)
  const allocate = useAllocatePayment(contractId)
  const delAlloc = useDeleteAllocation(contractId)
  const delPayment = useDeletePayment(contractId)
  const refundReg = useRegisterRefund(contractId)
  const confirm = useConfirm()

  const productLabel = useMemo(() => {
    const m = new Map<number, string>()
    elements.forEach(e => m.set(e.id, e.productLabel))
    return (elementId: number) => m.get(elementId) ?? `#${elementId}`
  }, [elements])

  const items = scheduleQ.data ?? []
  const payments = paymentsQ.data ?? []
  const paymentsTotal = payments.reduce((s, p) => s + p.amount, 0)

  const trancheLabel = useMemo(() => {
    const m = new Map<number, string>()
    items.forEach(i => m.set(i.id, `${productLabel(i.contractElementId)} · ${i.label ?? `#${i.seq}`}`))
    return (trancheId: number) => m.get(trancheId) ?? `#${trancheId}`
  }, [items, productLabel])

  // formularz wpłaty
  const [amountVal, setAmountVal] = useState<number | null>(null)
  const [paidAt, setPaidAt] = useState(today())
  const [mode, setMode] = useState<'AUTO' | 'MANUAL'>('AUTO')
  const [manual, setManual] = useState<Record<number, number | null>>({})
  // realokacja ręczna istniejącej wpłaty (id wpłaty w trybie ręcznym + kwoty per transza)
  const [reallocId, setReallocId] = useState<number | null>(null)
  const [reallocManual, setReallocManual] = useState<Record<number, number | null>>({})
  // rodzaj operacji w jednym panelu: wpłata vs zwrot
  const [opType, setOpType] = useState<'PAYMENT' | 'REFUND'>('PAYMENT')

  const unpaid = items.filter(i => paidOf(i) < i.amount - 0.005)

  // Harmonogram nieaktualny gdy jego suma != bieżąca gotówka umowy (np. po zmianie kwoty gotówki).
  const scheduleTotal = items.reduce((s, i) => s + i.amount, 0)
  const scheduleStale = items.length > 0 && Math.abs(scheduleTotal - cashTotal) > 0.005

  // Nadpłata: wpłacono więcej niż wynosi harmonogram → nadwyżka do zwrotu.
  const refund = items.length > 0 ? Math.max(0, paymentsTotal - scheduleTotal) : 0

  // Terminy: ile po terminie i najbliższa wymagana wpłata.
  const overdueItems = items.filter(i => i.dueDate && (i.amount - paidOf(i)) > 0.005 && daysUntil(i.dueDate) < 0)
  const overdueAmount = overdueItems.reduce((s, i) => s + (i.amount - paidOf(i)), 0)
  const nextDue = items
    .filter(i => i.dueDate && (i.amount - paidOf(i)) > 0.005 && daysUntil(i.dueDate) >= 0)
    .sort((a, b) => ((a.dueDate ?? '') < (b.dueDate ?? '') ? -1 : 1))[0]

  const doGenerate = () => {
    if (cashTotal <= 0) { toast.error('Najpierw ustaw kwotę gotówki umowy'); return }
    generate.mutate(cashTotal, { onError: () => toast.error('Nie udało się wygenerować harmonogramu') })
  }

  const doRegister = () => {
    if (amountVal == null || amountVal <= 0) { toast.error('Podaj kwotę wpłaty'); return }
    if (mode === 'AUTO') {
      register.mutate({ paidAt, amount: amountVal, mode: 'AUTO' }, {
        onSuccess: () => { setAmountVal(null) }, onError: () => toast.error('Nie udało się zapisać wpłaty'),
      })
    } else {
      // Bez błędu przy nadwyżce - backend kapuje alokację do reszty transzy i kwoty wpłaty,
      // a nadwyżka zostaje niezaalokowana (widoczna na wpłacie).
      const allocations = unpaid
        .map(i => ({ trancheId: i.id, amount: manual[i.id] ?? 0 }))
        .filter(a => a.amount > 0)
      register.mutate({ paidAt, amount: amountVal, mode: 'MANUAL', allocations }, {
        onSuccess: () => { setAmountVal(null); setManual({}) }, onError: () => toast.error('Nie udało się zapisać wpłaty'),
      })
    }
  }

  const doReallocateAuto = (paymentId: number) => {
    allocate.mutate({ paymentId, input: { mode: 'AUTO' } },
      { onError: () => toast.error('Nie udało się zrealokować wpłaty') })
  }

  const toggleManualRealloc = (paymentId: number) => {
    setReallocId(prev => (prev === paymentId ? null : paymentId))
    setReallocManual({})
  }

  const doReallocateManual = (paymentId: number) => {
    const allocations = unpaid
      .map(i => ({ trancheId: i.id, amount: reallocManual[i.id] ?? 0 }))
      .filter(a => a.amount > 0)
    if (allocations.length === 0) { toast.error('Wpisz kwoty alokacji do transz'); return }
    allocate.mutate({ paymentId, input: { mode: 'MANUAL', allocations } }, {
      onSuccess: () => { setReallocId(null); setReallocManual({}) },
      onError: () => toast.error('Nie udało się zrealokować wpłaty'),
    })
  }

  const switchOp = (t: 'PAYMENT' | 'REFUND') => {
    setOpType(t)
    // przy przełączeniu na zwrot podpowiedz kwotę nadpłaty (gdy pole puste)
    if (t === 'REFUND' && refund > 0.005 && (amountVal == null || amountVal <= 0)) setAmountVal(round2(refund))
  }
  const doRegisterRefund = () => {
    if (amountVal == null || amountVal <= 0) { toast.error('Podaj kwotę zwrotu'); return }
    refundReg.mutate({ amount: amountVal, refundedAt: paidAt }, {
      onSuccess: () => { setAmountVal(null); setOpType('PAYMENT') },
      onError: () => toast.error('Nie udało się zapisać zwrotu'),
    })
  }

  const doDeletePayment = async (paymentId: number) => {
    const ok = await confirm({
      title: 'Usunąć wpłatę?',
      description: 'Wpłata i jej alokacje zostaną usunięte, a transze wrócą do niezapłaconych.',
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (!ok) return
    delPayment.mutate(paymentId, { onError: () => toast.error('Nie udało się usunąć wpłaty') })
  }

  const doDeleteAllocation = async (paymentId: number, allocationId: number) => {
    const ok = await confirm({
      title: 'Usunąć alokację?',
      description: 'Transza wróci do niezapłaconej, a kwota wpłaty znów będzie do alokacji.',
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (!ok) return
    delAlloc.mutate({ paymentId, allocationId },
      { onError: () => toast.error('Nie udało się usunąć alokacji') })
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
        <div>
          <h3 className="text-base font-semibold tracking-tight text-slate-900">Harmonogram gotówki</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Generowany na umowie z szablonu konfiguracji; gotówka dzielona na produkty. Wpłaty alokowane na transze.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button type="button" className={btnCls} onClick={doGenerate} disabled={generate.isPending}>
            {scheduleStale ? 'Regeneruj' : 'Generuj'} harmonogram ({formatCurrencyPl(cashTotal)})
          </button>
          {scheduleStale && (
            <span className="text-[11px] font-medium text-amber-700">
              ⚠ Nieaktualny: suma transz {formatCurrencyPl(scheduleTotal)} ≠ gotówka {formatCurrencyPl(cashTotal)}
            </span>
          )}
        </div>
      </header>

      {/* Podgląd podziału gotówki na produkty (informacyjnie - wg czego policzy się harmonogram) */}
      {cashTotal > 0 && splitQ.data && splitQ.data.items.length > 0 && (() => {
        const split = splitQ.data
        const missing = split.items.filter(it => it.price == null)
        const partial = split.byPrice && missing.length > 0
        return (
          <div className="border-b border-slate-100 px-6 py-4">
            <div className="text-xs font-semibold text-slate-600 mb-2">
              Podział gotówki na produkty {split.byPrice ? '(proporcjonalnie do ceny produktu)' : '(równo - brak cen produktów)'}
            </div>
            {!split.byPrice && (
              <p className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-700">
                ⚠ Żaden produkt nie ma wypełnionej ceny (pole z rolą „cena elementu"). Gotówka zostanie podzielona równo
                po liczbie produktów.
              </p>
            )}
            {partial && (
              <p className="mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-700">
                ⚠ Brak ceny dla: {missing.map(it => productLabel(it.contractElementId)).join(', ')} - {missing.length === 1 ? 'ten produkt dostanie' : 'te produkty dostaną'} 0 zł gotówki,
                a całość trafi na produkty z ceną. Uzupełnij cenę lub rozdziel wpłaty ręcznie.
              </p>
            )}
            <table className="w-full max-w-md text-xs">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1 font-medium">Produkt</th>
                  <th className="py-1 font-medium text-right">Cena</th>
                  <th className="py-1 font-medium text-right">Gotówka</th>
                </tr>
              </thead>
              <tbody>
                {split.items.map(it => {
                  const noPrice = split.byPrice && it.price == null
                  return (
                    <tr key={it.contractElementId} className={`border-t border-slate-100 ${noPrice ? 'bg-amber-50/40' : ''}`}>
                      <td className="py-1 text-slate-700">{productLabel(it.contractElementId)}</td>
                      <td className={`py-1 text-right tabular-nums ${noPrice ? 'text-amber-700' : 'text-slate-500'}`}>
                        {it.price != null ? formatCurrencyPl(it.price) : 'brak'}
                      </td>
                      <td className="py-1 text-right tabular-nums font-medium text-slate-800">
                        {formatCurrencyPl(it.allocatedCash)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-6 py-5">
        {/* LEWA: harmonogram */}
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60 text-xs font-semibold text-slate-600">
            Harmonogram ({items.length} transz)
          </div>
          {(overdueAmount > 0 || nextDue) && (
            <div className="px-3 py-2 border-b border-slate-100 flex flex-col gap-0.5 text-[11px]">
              {overdueAmount > 0 && (
                <span className="font-medium text-rose-700">
                  ⚠ Po terminie: {formatCurrencyPl(overdueAmount)} ({overdueItems.length} {overdueItems.length === 1 ? 'transza' : 'transz'})
                </span>
              )}
              {nextDue && (
                <span className="text-slate-600">
                  Najbliższy termin: {formatDatePl(nextDue.dueDate!)} ({daysUntil(nextDue.dueDate!) === 0 ? 'dziś' : `za ${daysUntil(nextDue.dueDate!)} ${dayWord(daysUntil(nextDue.dueDate!))}`}) · {formatCurrencyPl(nextDue.amount - paidOf(nextDue))}
                </span>
              )}
            </div>
          )}
          {items.length === 0 ? (
            <p className="px-3 py-6 text-xs text-slate-500">Brak harmonogramu - wygeneruj powyżej.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50/40 text-left text-slate-600">
                <tr>
                  <th className="px-3 py-2 font-semibold">Produkt</th>
                  <th className="px-3 py-2 font-semibold">Transza</th>
                  <th className="px-3 py-2 font-semibold text-right">Kwota</th>
                  <th className="px-3 py-2 font-semibold text-right">Pozostało</th>
                  <th className="px-3 py-2 font-semibold">Termin</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map(i => {
                  const st = status(i)
                  const ti = termInfo(i)
                  const remaining = Math.max(0, round2(i.amount - paidOf(i)))
                  return (
                    <tr key={i.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-700">{productLabel(i.contractElementId)}</td>
                      <td className="px-3 py-2">{i.label ?? `#${i.seq}`}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{formatCurrencyPl(i.amount)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${remaining <= 0.005 ? 'text-emerald-700' : 'text-slate-700 font-medium'}`}>
                        {remaining <= 0.005 ? '✓' : formatCurrencyPl(remaining)}
                      </td>
                      <td className={`px-3 py-2 whitespace-nowrap ${ti.cls}`}>
                        {ti.label}
                        {ti.hint && <span className="block text-[10px]">{ti.hint}</span>}
                      </td>
                      <td className={`px-3 py-2 ${st.cls}`}>{st.label}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* PRAWA: wpłaty */}
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs font-semibold text-slate-600">Zarejestruj operację</div>
              <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11px] font-medium">
                <button type="button" onClick={() => switchOp('PAYMENT')}
                  className={`rounded px-2.5 py-1 ${opType === 'PAYMENT' ? 'bg-white text-sky-700 shadow-xs' : 'text-slate-500 hover:text-slate-700'}`}>
                  Wpłata
                </button>
                <button type="button" onClick={() => switchOp('REFUND')}
                  className={`rounded px-2.5 py-1 ${opType === 'REFUND' ? 'bg-white text-rose-700 shadow-xs' : 'text-slate-500 hover:text-slate-700'}`}>
                  Zwrot
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-slate-600">Data</label>
                <input type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)} className={inputCls} />
              </div>
              {opType === 'PAYMENT' && (
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] font-medium text-slate-600">Tryb</label>
                  <select value={mode} onChange={e => setMode(e.target.value as 'AUTO' | 'MANUAL')} className={inputCls}>
                    <option value="AUTO">Auto (proporcjonalnie)</option>
                    <option value="MANUAL">Ręcznie</option>
                  </select>
                </div>
              )}
              <div className="flex flex-col gap-1 w-36">
                <label className="text-[11px] font-medium text-slate-600">{opType === 'PAYMENT' ? 'Kwota wpłaty' : 'Kwota zwrotu'}</label>
                <NumericInputBase value={amountVal}
                  onChange={v => setAmountVal(v as number | null)} decimals={2} suffix="zł" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-transparent select-none" aria-hidden="true">.</span>
                {opType === 'PAYMENT' ? (
                  <button type="button" className={`${btnCls} mb-1`} onClick={doRegister} disabled={register.isPending}>
                    Zapisz wpłatę
                  </button>
                ) : (
                  <button type="button" className={`${btnCls} mb-1 bg-rose-600 hover:bg-rose-700`} onClick={doRegisterRefund} disabled={refundReg.isPending}>
                    Zapisz zwrot
                  </button>
                )}
              </div>
            </div>

            {opType === 'REFUND' && refund > 0.005 && (
              <p className="mt-2 text-[11px] text-rose-700">
                Nadpłata do zwrotu: {formatCurrencyPl(refund)}
                {amountVal !== round2(refund) && (
                  <button type="button" className="ml-1 underline hover:no-underline" onClick={() => setAmountVal(round2(refund))}>
                    wstaw
                  </button>
                )}
              </p>
            )}

            {opType === 'PAYMENT' && mode === 'MANUAL' && (
              <div className="mt-3 border-t border-slate-100 pt-2">
                <div className="text-[11px] text-slate-500 mb-1">
                  Alokacja na transze (może być częściowa - reszta zostanie niezaalokowana):
                </div>
                {unpaid.length === 0 ? (
                  <p className="text-[11px] text-slate-500">Brak niezapłaconych transz.</p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {unpaid.map(i => (
                      <div key={i.id} className="flex items-center gap-2 text-[11px]">
                        <span className="flex-1 truncate text-slate-600">
                          {productLabel(i.contractElementId)} · {i.label ?? `#${i.seq}`} ({formatCurrencyPl(i.amount - paidOf(i))} do zapłaty)
                        </span>
                        <button type="button" className="text-sky-700 hover:text-sky-900" title="Wstaw kwotę transzy"
                          onClick={() => setManual(m => ({ ...m, [i.id]: round2(i.amount - paidOf(i)) }))}>→</button>
                        <div className="w-28">
                          <NumericInputBase value={manual[i.id] ?? null}
                            onChange={v => setManual(m => ({ ...m, [i.id]: v as number | null }))} decimals={2} suffix="zł" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60 text-xs font-semibold text-slate-600 flex items-center justify-between gap-2">
              <span>Wpłaty ({payments.length})</span>
              <span className="tabular-nums">
                Razem: {formatCurrencyPl(paymentsTotal)}
                {refund > 0.005 && (
                  <span className="ml-2 text-rose-700">· nadpłata {formatCurrencyPl(refund)} do zwrotu</span>
                )}
              </span>
            </div>
            {payments.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-500">Brak wpłat.</p>
            ) : (
              <table className="w-full text-xs">
                <tbody>
                  {payments.map(p => {
                    const isRefund = p.amount < 0
                    const ps = paymentStatus(p, unpaid.length > 0)
                    return (
                      <Fragment key={p.id}>
                        <tr className="border-t border-slate-100 first:border-0">
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{p.paidAt}</td>
                          <td className="px-3 py-2 text-slate-500 truncate max-w-[8rem]">{p.label ?? (isRefund ? 'Zwrot nadpłaty' : '—')}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium whitespace-nowrap">{formatCurrencyPl(p.amount)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {isRefund
                              ? <span className="text-rose-700 font-medium">Zwrot</span>
                              : <span className={ps.cls}>{ps.label}</span>}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {!isRefund && ps.needsAlloc && (
                              <>
                                <button type="button"
                                  className="text-sky-700 hover:text-sky-900 font-medium disabled:opacity-50"
                                  onClick={() => doReallocateAuto(p.id)} disabled={allocate.isPending}
                                  title="Rozdziel resztę automatycznie">Auto</button>
                                <button type="button"
                                  className="ml-2 text-sky-700 hover:text-sky-900 font-medium"
                                  onClick={() => toggleManualRealloc(p.id)}
                                  title="Rozdziel resztę ręcznie">Ręcznie</button>
                              </>
                            )}
                            <button type="button"
                              className="ml-2 text-rose-600 hover:text-rose-800 font-medium disabled:opacity-50"
                              onClick={() => doDeletePayment(p.id)} disabled={delPayment.isPending}
                              title="Usuń wpłatę">Usuń</button>
                          </td>
                        </tr>
                        {!isRefund && p.allocations.map(a => (
                          <tr key={`a${a.id}`} className="bg-slate-50/50 text-[11px]">
                            <td className="px-3 py-1 text-slate-500" colSpan={2}>↳ {trancheLabel(a.trancheId)}</td>
                            <td className="px-3 py-1 text-right tabular-nums text-slate-600">{formatCurrencyPl(a.amount)}</td>
                            <td className="px-3 py-1 text-slate-400">alokacja</td>
                            <td className="px-3 py-1 text-right">
                              <button type="button"
                                className="text-rose-600 hover:text-rose-800 font-medium disabled:opacity-50"
                                onClick={() => doDeleteAllocation(p.id, a.id)} disabled={delAlloc.isPending}
                                title="Usuń alokację">✕</button>
                            </td>
                          </tr>
                        ))}
                        {reallocId === p.id && (
                          <tr className="bg-sky-50/40">
                            <td colSpan={5} className="px-3 py-2">
                              <div className="text-[11px] text-slate-600 mb-1">
                                Ręczna alokacja reszty ({formatCurrencyPl(p.unallocatedAmount)} do rozdysponowania):
                              </div>
                              {unpaid.length === 0 ? (
                                <p className="text-[11px] text-slate-500">Brak niezapłaconych transz.</p>
                              ) : (
                                <div className="flex flex-col gap-1">
                                  {unpaid.map(i => (
                                    <div key={i.id} className="flex items-center gap-2 text-[11px]">
                                      <span className="flex-1 truncate text-slate-600">
                                        {trancheLabel(i.id)} ({formatCurrencyPl(i.amount - paidOf(i))} do zapłaty)
                                      </span>
                                      <button type="button" className="text-sky-700 hover:text-sky-900" title="Wstaw kwotę transzy"
                                        onClick={() => setReallocManual(m => ({ ...m, [i.id]: round2(i.amount - paidOf(i)) }))}>→</button>
                                      <div className="w-28">
                                        <NumericInputBase value={reallocManual[i.id] ?? null}
                                          onChange={v => setReallocManual(m => ({ ...m, [i.id]: v as number | null }))}
                                          decimals={2} suffix="zł" />
                                      </div>
                                    </div>
                                  ))}
                                  <div className="flex justify-end gap-3 mt-1">
                                    <button type="button" className="text-[11px] text-slate-500 hover:text-slate-700"
                                      onClick={() => toggleManualRealloc(p.id)}>Anuluj</button>
                                    <button type="button" className={btnCls}
                                      onClick={() => doReallocateManual(p.id)} disabled={allocate.isPending}>
                                      Zapisz alokację
                                    </button>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
